import { useState, type ReactNode } from "react";

import { urlDoLogoDoEscritorio } from "@/lib/api/perfil-do-escritorio";
import type { Session, VarianteDoLogo } from "@/lib/types";
import { useThemePreference } from "@/lib/useThemePreference";
import { cn } from "@/lib/utils";

/**
 * TUT-512 — a PORTA ÚNICA do logo do escritório na tela (regra P53).
 *
 * A barra do app, o portal, o cabeçalho de impressão dos relatórios e a
 * prévia do perfil desenham o logo por aqui, e só por aqui: qual variante
 * (clara no papel e no tema claro; escura no tema escuro e, sem ela, a clara
 * num ladrilho branco), a URL com a versão, o `alt` com o nome do escritório
 * e a RESERVA quando não há logo ou a imagem falha (o monograma na
 * impressão, as iniciais no topo do portal, nada na barra). Tela que monta
 * `<img>` do logo à mão reprova em `marca-do-escritorio.test.ts`.
 */
export type SuperficieDaMarca = "papel" | "tema";

export function escolherVarianteDoLogo(
  session: Session | null | undefined,
  superficie: SuperficieDaMarca,
  temaEscuro: boolean,
): { variante: VarianteDoLogo; ladrilhoClaro: boolean } | null {
  const logo = session?.tenant.logo;
  if (!logo?.light && !logo?.dark) return null;
  if (superficie === "papel") return logo.light ? { variante: "light", ladrilhoClaro: false } : null;
  if (temaEscuro) {
    if (logo.dark) return { variante: "dark", ladrilhoClaro: false };
    return logo.light ? { variante: "light", ladrilhoClaro: true } : null;
  }
  return logo.light ? { variante: "light", ladrilhoClaro: false } : null;
}

const CONECTIVOS = new Set(["e", "de", "da", "do", "das", "dos", "&"]);

/**
 * TUT-546 — as INICIAIS do escritório, para o selo que fica no lugar do logo
 * no topo do portal: até duas letras, sem os conectivos ("Marçal & Hespanhol"
 * → "MH"). O monograma da impressão ("M&H") é outra peça, a do papel.
 */
export function iniciaisDoEscritorio(nome: string): string {
  const palavras = nome.split(/\s+/u).filter((palavra) => palavra && !CONECTIVOS.has(palavra.toLowerCase()));
  return palavras
    .slice(0, 2)
    .map((palavra) => palavra[0]?.toLocaleUpperCase("pt-BR") ?? "")
    .join("");
}

/** O selo das iniciais: o azul da marca, como o avatar do menu da conta. */
function SeloDasIniciais({ nome, className }: { nome: string; className?: string }) {
  return (
    <span
      data-marca-iniciais=""
      aria-hidden="true"
      className={cn(
        "inline-grid aspect-square place-items-center rounded-lg border border-[var(--brand-border)] bg-[var(--brand-blue)] text-legenda font-semibold text-white",
        className,
      )}
    >
      {iniciaisDoEscritorio(nome)}
    </span>
  );
}

export function MarcaDoEscritorio({
  session,
  nome,
  superficie = "tema",
  className,
  reserva = null,
  decorativa = false,
  variante,
}: {
  session: Session | null | undefined;
  /** O nome do escritório, para o `alt` (imagem bloqueada ou leitor de tela). */
  nome: string;
  superficie?: SuperficieDaMarca;
  /** Altura e largura máximas: a tela decide o tamanho, a peça decide o resto. */
  className?: string;
  /**
   * O que desenhar sem logo (ou se a imagem não carregar). `"iniciais"` é o
   * selo com as iniciais do `nome` (TUT-546, o topo do portal).
   */
  reserva?: ReactNode | "iniciais";
  /** O nome já está escrito ao lado: o logo vira enfeite (`alt` vazio) e não se lê duas vezes. */
  decorativa?: boolean;
  /** Uma variante certa, sem escolha pelo tema (a amostra de cada logo no perfil). */
  variante?: VarianteDoLogo;
}) {
  const { theme } = useThemePreference();
  const escolha = variante
    ? session?.tenant.logo?.[variante]
      ? { variante, ladrilhoClaro: false }
      : null
    : escolherVarianteDoLogo(session, superficie, superficie === "tema" && theme === "dark");
  const url = escolha ? urlDoLogoDoEscritorio(session, escolha.variante) : null;
  const [falhou, setFalhou] = useState<string | null>(null);
  if (!escolha || !url || falhou === url)
    return reserva === "iniciais" ? <SeloDasIniciais nome={nome} className={className} /> : <>{reserva}</>;
  const imagem = (
    <img
      data-marca-do-escritorio={escolha.variante}
      src={url}
      alt={decorativa ? "" : nome}
      onError={() => setFalhou(url)}
      className={cn("block h-full w-auto max-w-full object-contain", escolha.ladrilhoClaro ? "" : className)}
      draggable={false}
    />
  );
  if (!escolha.ladrilhoClaro) return imagem;
  return (
    <span
      data-marca-ladrilho-claro=""
      className={cn("inline-flex items-center rounded-md bg-[var(--tutoo-white)] px-1.5 py-1", className)}
    >
      {imagem}
    </span>
  );
}
